import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RotateCcw, ArrowLeft } from 'lucide-react';
import { Card, EmptyState, GroupByDropdown, FilterBar, ViewSwitcher, CalendarNavigation, ListDateCarousel, PrintButton } from '@/components/ui';
import { WeeklyView, MonthlyView } from '@/components/calendar';
import { useItemStore, useUIStore, useSettingsStore } from '@/stores';
import { ChoreWheelList } from './ChoreWheelList';
import { isRecurringItem, extendItemOccurrences } from '@perfin/shared';
import type { TaskItem } from '@perfin/shared';
import type { TabKey } from '@/stores/uiStore';

// Extended TaskItem with choreWheel field
type ChoreTask = TaskItem & { choreWheel?: boolean };

export function ChoreWheelPage() {
  const navigate = useNavigate();
  const tasks = useItemStore((state) => state.getTasks());
  const { activeTab, setActiveTab, categoryFilters, pageFilters, viewMode, calendarOffset, groupBy } = useUIStore();
  const { currentUserId, members, getCategoriesForType } = useSettingsStore();
  const isAdmin = useSettingsStore((state) => state.currentUserRole === 'admin' || state.currentUserRole === 'superAdmin');

  const currentViewMode = viewMode?.choreWheel ?? 'card';
  const currentTab = activeTab?.choreWheel ?? 'today';
  const currentGroupBy = groupBy?.choreWheel ?? 'owner';
  const pFilters = pageFilters?.choreWheel ?? { ownerFilter: null, statusFilter: null, priorityFilter: null };

  // Calendar views show all chores by occurrence
  const isCalendarView = currentViewMode === 'weekly' || currentViewMode === 'monthly';
  const effectiveTab = isCalendarView ? 'allScheduledAndRecurring' : currentTab;

  // Bug #2: Auto-switch groupBy when 'date' is invalid for All tab
  const { setGroupBy } = useUIStore();
  React.useEffect(() => {
    if (currentTab === 'all' && currentGroupBy === 'date') {
      setGroupBy('choreWheel', 'owner');
    }
  }, [currentTab, currentGroupBy, setGroupBy]);

  // Bug #5: Validate owner filter - if references non-existent user, reset to current user
  const { setOwnerFilter } = useUIStore();
  React.useEffect(() => {
    if (pFilters.ownerFilter && members.length > 0) {
      const ownerExists = members.some(m => m.userId === pFilters.ownerFilter);
      if (!ownerExists && currentUserId) {
        setOwnerFilter('choreWheel', currentUserId);
      }
    }
  }, [pFilters.ownerFilter, members, currentUserId, setOwnerFilter]);

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'all', label: 'All Chores' },
    { key: 'pastWeek', label: 'Past Week' },
    { key: 'today', label: 'Today' },
    { key: 'nextWeek', label: 'Next Week' },
  ];

  // Get reference date from store
  const getReferenceDate = useUIStore((state) => state.getReferenceDate);
  const referenceDate = getReferenceDate('choreWheel');

  // Filter to chore wheel tasks only
  const choreTasks = useMemo(() => {
    return tasks.filter((t) => (t as ChoreTask).choreWheel === true);
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    const refDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());
    const refDatePlusOne = new Date(refDate.getTime() + 24 * 60 * 60 * 1000);
    const sevenDaysBefore = new Date(refDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    const sevenDaysAfter = new Date(refDate.getTime() + 7 * 24 * 60 * 60 * 1000);

    const parseDate = (d: unknown): Date | null => {
      if (!d) return null;
      if (typeof (d as { toDate?: () => Date }).toDate === 'function') {
        return (d as { toDate: () => Date }).toDate();
      }
      if (d instanceof Date) return d;
      if (typeof d === 'object' && d !== null && 'seconds' in d) {
        return new Date((d as { seconds: number }).seconds * 1000);
      }
      return new Date(d as string | number);
    };

    let result = choreTasks;

    // 1. Apply filters FIRST (category, status, priority)
    const filter = categoryFilters.choreWheel;
    if (filter?.categoryId) {
      result = result.filter((task) => task.category === filter.categoryId);
    }
    if (filter?.subCategoryId) {
      result = result.filter((task) => task.subCategory === filter.subCategoryId);
    }
    if (pFilters?.itemGroupFilter) {
      result = result.filter((task) => task.itemGroup === pFilters.itemGroupFilter);
    }

    // Status filter: for time-based tabs, only apply to non-recurring items
    const isTimeBased = effectiveTab === 'pastWeek' || effectiveTab === 'today' || effectiveTab === 'nextWeek';
    if (pFilters.statusFilter) {
      result = result.filter((task) => {
        if (isTimeBased && isRecurringItem(task)) {
          return true; // Let ChoreWheelList filter by occurrence status
        }
        return task.status === pFilters.statusFilter;
      });
    }

    if (pFilters.priorityFilter) {
      result = result.filter((task) => task.priority === pFilters.priorityFilter);
    }

    // Owner filtering
    if (pFilters?.ownerFilter) {
      result = result.filter((task) => task.owner === pFilters.ownerFilter);
    }

    // RBAC: Non-admin users only see chores they own or created (when no owner filter set)
    if (!isAdmin && currentUserId && !pFilters?.ownerFilter) {
      result = result.filter((task) => task.owner === currentUserId || task.createdBy === currentUserId);
    }

    // 2. Apply tab (time) filter on the result
    switch (effectiveTab) {
      case 'all':
        // All chores - NO tile replication (show one tile per chore item)
        // All recurring chores are included without occurrence expansion
        break;

      case 'allScheduledAndRecurring':
        // For calendar views: include all recurring chores
        result = result.filter((task) => isRecurringItem(task));
        break;

      case 'pastWeek':
        // Recurring chores with occurrences in the past 7 days
        result = result.filter((task) => {
          if (isRecurringItem(task)) {
            const occurrences = (task as unknown as { occurrences?: Array<{ date: unknown }> }).occurrences || [];
            return occurrences.some(occ => {
              const occDate = parseDate(occ.date);
              return occDate && occDate >= sevenDaysBefore && occDate < refDate;
            });
          }
          return false; // Non-recurring chores don't show in time-based tabs
        });
        break;

      case 'today':
        // Recurring chores with occurrences on refDate
        result = result.filter((task) => {
          if (isRecurringItem(task)) {
            const occurrences = (task as unknown as { occurrences?: Array<{ date: unknown }> }).occurrences || [];
            return occurrences.some(occ => {
              const occDate = parseDate(occ.date);
              return occDate && occDate >= refDate && occDate < refDatePlusOne;
            });
          }
          return false;
        });
        break;

      case 'nextWeek':
        // Recurring chores with occurrences in the next 7 days
        result = result.filter((task) => {
          if (isRecurringItem(task)) {
            const occurrences = (task as unknown as { occurrences?: Array<{ date: unknown }> }).occurrences || [];
            return occurrences.some(occ => {
              const occDate = parseDate(occ.date);
              return occDate && occDate >= refDatePlusOne && occDate <= sevenDaysAfter;
            });
          }
          return false;
        });
        break;
    }

    return result;
  }, [choreTasks, effectiveTab, categoryFilters.choreWheel, pFilters, referenceDate, isAdmin, currentUserId]);

  // Handler for extending occurrences when navigating beyond 180-day horizon
  const handleNavigateBeyondHorizon = useCallback(async (targetDate: Date) => {
    const recurringIds = choreTasks
      .filter((task) => isRecurringItem(task))
      .map((task) => task.id);

    if (recurringIds.length > 0) {
      try {
        await extendItemOccurrences(recurringIds, targetDate);
      } catch (error) {
        console.error('Failed to extend occurrences:', error);
      }
    }
  }, [choreTasks]);

  // Helper functions for PDF generation
  const taskCategories = getCategoriesForType('task');
  const getCategoryName = (categoryId: string) => {
    const cat = taskCategories.find((c) => c.id === categoryId);
    return cat?.name || categoryId;
  };
  const getOwnerName = (ownerId: string) => {
    const member = members.find((m) => m.userId === ownerId);
    return member?.displayName || ownerId;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Chore Wheel</h1>
          <PrintButton
            title="Chore Wheel"
            items={filteredTasks}
            getCategoryName={getCategoryName}
            getOwnerName={getOwnerName}
          />
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-4">
        <FilterBar itemType="task" pageKey="choreWheel" hideStatusFilter={currentTab === 'all'} />
        <hr className="w-full border-gray-300 md:hidden" />
        <div className="flex items-center gap-3 flex-wrap md:flex-nowrap">
          {currentViewMode === 'card' && <GroupByDropdown pageKey="choreWheel" includeOwner excludeOptions={currentTab === 'all' ? ['date'] : []} />}
          <ViewSwitcher pageKey="choreWheel" />
        </div>
      </div>

      <div className="flex items-center justify-between border-b border-gray-200">
        <div className="flex gap-1 flex-wrap md:flex-nowrap">
          {/* In calendar view, show single "All Scheduled and Recurring" tab */}
          {isCalendarView ? (
            <button className="px-4 py-2 text-sm font-medium border-b-2 border-primary-600 text-primary-600">
              All Chores by Occurrence
            </button>
          ) : (
            tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab('choreWheel', tab.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  currentTab === tab.key
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))
          )}
        </div>
        {currentViewMode === 'card' && (currentTab === 'pastWeek' || currentTab === 'today' || currentTab === 'nextWeek') && (
          <ListDateCarousel pageKey="choreWheel" />
        )}
        {currentViewMode !== 'card' && (
          <CalendarNavigation
            pageKey="choreWheel"
            mode={currentViewMode}
            onNavigateBeyondHorizon={handleNavigateBeyondHorizon}
          />
        )}
      </div>

      {currentViewMode === 'card' && (
        filteredTasks.length === 0 ? (
          <Card>
            <EmptyState
              icon={<RotateCcw className="h-12 w-12" />}
              title="No chores found"
              description="Create chore tasks using the Tasks page and toggle 'Make Chore'"
            />
          </Card>
        ) : (
          <ChoreWheelList tasks={filteredTasks} groupBy={currentGroupBy} currentTab={currentTab} hideStatusToggle={currentTab === 'all'} referenceDate={referenceDate} statusFilter={pFilters.statusFilter} />
        )
      )}

      {currentViewMode === 'weekly' && (
        <WeeklyView
          items={filteredTasks}
          offset={calendarOffset.choreWheel.weekly}
          basePath="/tasks"
        />
      )}

      {currentViewMode === 'monthly' && (
        <MonthlyView
          items={filteredTasks}
          offset={calendarOffset.choreWheel.monthly}
          basePath="/tasks"
        />
      )}
    </div>
  );
}
